// Documents — Secure Document Vault (Phase 2.4).
// Branded org-scoped screen: list / upload / download, with L4 view-only.
// The storage provider/endpoint is NEVER exposed to the browser — download
// and view both call the API in ?proxy=true mode, which streams bytes
// through the backend rather than handing back a presigned storage URL.
// This screen is the only client access path.
//
// Endpoints used (backend Plans 02.4-05 / 02.4-06):
//   GET  /api/documents                 — list org documents (storage_key hidden)
//   POST /api/documents                 — multipart upload (file, category, classification, [location_id, vendor_id, department])
//   GET  /api/documents/{id}/download?proxy=true — non-L4 → streamed bytes, attachment
//   GET  /api/documents/{id}/view?proxy=true     — L4 → streamed bytes, inline; L4 download is refused server-side
//
// There is no category-enumeration endpoint in v1 (the admin policy editor is
// deferred, D-02). Categories are seeded server-side per org; the upload form
// uses a free-text category input (suggested via a <datalist> built from the
// docs already present) plus a classification select (L1–L4). An unknown
// category returns 422 ("Unknown document category"), surfaced inline.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldCheck, Upload, FileText, Download, Eye, Lock, Loader2, AlertTriangle, FolderOpen, Trash2,
} from 'lucide-react';
import { apiErrorMessage } from './utils/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASSIFICATIONS = ['L1', 'L2', 'L3', 'L4'];

const CLASS_LABEL = {
  L1: 'L1 · Public',
  L2: 'L2 · Internal',
  L3: 'L3 · Confidential',
  L4: 'L4 · Restricted',
};

// Brand-token badge styles per classification (no raw colours — Tailwind tokens).
const classBadge = (c) =>
  c === 'L4' ? 'bg-danger/10 text-danger border-danger/30'
  : c === 'L3' ? 'bg-warn/10 text-warn border-warn/30'
  : c === 'L2' ? 'bg-primary-50 text-primary-700 border-primary-500/30'
  : 'bg-ok/10 text-ok border-ok/30';

const fmtBytes = (b) => {
  if (b === null || b === undefined || b === '') return '—';
  const n = typeof b === 'number' ? b : parseInt(b, 10);
  if (Number.isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

// Resolve the active org id from /api/platform/context (mirrors CostIntelligence).
// Returned headers always include X-Organization-Id once known.
const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });

  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) {
          setOrgId(id);
          try { localStorage.setItem('current_organization_id', id); } catch { /* ignore */ }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  }), [token, orgId]);

  return { headers, orgId };
};

const Documents = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // Upload form
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('');
  const [classification, setClassification] = useState('L2');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { ok, id } | { error }

  // Per-row action state (download / view) keyed by document id.
  const [actionBusy, setActionBusy] = useState(null);
  const [actionError, setActionError] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers: authHeaders } = useOrgScopedHeaders(token);

  const load = useCallback(async () => {
    if (!authHeaders['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    setListError(null);
    try {
      const res = await fetch(`${API_URL}/api/documents`, { headers: authHeaders });
      if (!res.ok) {
        setListError('Could not load documents.');
        setDocs([]);
        return;
      }
      const body = await res.json();
      setDocs(Array.isArray(body) ? body : (body.documents || []));
    } catch {
      setListError('Could not load documents.');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Suggested categories: distinct categories already present in this org's docs.
  const knownCategories = useMemo(() => {
    const set = new Set();
    for (const d of docs) { if (d.category) set.add(d.category); }
    return Array.from(set).sort();
  }, [docs]);

  const upload = async () => {
    if (!file || !category || !classification) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      fd.append('classification', classification);
      // Drop JSON Content-Type so the browser sets the multipart boundary.
      const { 'Content-Type': _ct, ...mpHeaders } = authHeaders;
      const res = await fetch(`${API_URL}/api/documents`, {
        method: 'POST',
        headers: mpHeaders,
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setUploadResult({ error: apiErrorMessage(err.detail, `Upload failed (${res.status})`) });
        return;
      }
      const body = await res.json();
      setUploadResult({ ok: true, id: body.id });
      setFile(null);
      // Reset the native file input by clearing its value via key remount.
      await load();
    } catch {
      setUploadResult({ error: 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  // Non-L4: stream the file through the backend (proxy) so download works even
  // when the storage endpoint isn't browser-reachable (e.g. on-prem MinIO).
  const download = async (doc) => {
    setActionBusy(doc.id);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.id}/download?proxy=true`, { headers: authHeaders });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Download refused (${res.status})`));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.id + (doc.content_type === 'application/pdf' ? '.pdf' : '');
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setActionError('Download failed.');
    } finally {
      setActionBusy(null);
    }
  };

  // Soft-delete: the record disappears from list/download/view (server-side,
  // documents/router.py) but the object and audit trail are untouched. Not
  // role-hidden here — the policy engine is the sole authority, so a caller
  // without the delete verb sees the same button and an honest 403, exactly
  // like every other denial this screen already surfaces (L4 view-only).
  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename || 'this document'}? This removes it from the vault.`)) {
      return;
    }
    setActionBusy(doc.id);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `Delete refused (${res.status})`));
        return;
      }
      await load();
    } catch {
      setActionError('Delete failed.');
    } finally {
      setActionBusy(null);
    }
  };

  // L4: stream the file inline through the backend (proxy) rather than
  // opening a presigned URL — same rationale as download() above. A
  // presigned URL points at the storage endpoint directly; on a deployment
  // where object storage isn't published to the internet, that URL is
  // reachable from this server but not from the operator's browser. The
  // audit entry and L4 dual-channel alert still fire server-side either way.
  const view = async (doc) => {
    setActionBusy(doc.id);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.id}/view?proxy=true`, { headers: authHeaders });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setActionError(apiErrorMessage(err.detail, `View refused (${res.status})`));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Give the new tab time to actually load the blob before revoking —
      // revoking immediately can race a large PDF's inline render.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setActionError('View failed.');
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="documents-page">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-ink-muted">Stay Compliant</div>
            <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-primary-500" />
              Secure Documents
            </h1>
            <p className="text-ink-muted text-sm mt-1">
              Org-scoped document vault. Files are served only through short-lived secure links — the
              storage provider is never exposed. Restricted (L4) documents are view-only.
            </p>
          </div>
        </div>

        {/* Upload zone */}
        <div className="bg-surface border border-surface-border rounded-lg p-5 mb-6" data-testid="documents-upload-zone">
          <div className="flex items-center gap-3 mb-4">
            <Upload className="w-4 h-4 text-ink-muted" />
            <h3 className="font-display text-sm">Upload a document</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">File</label>
              <input aria-label="File"
                key={uploadResult?.ok ? `cleared-${uploadResult.id}` : 'file-input'}
                type="file"
                accept=".pdf,.docx,.xlsx,.csv,image/jpeg,image/png,image/webp"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setUploadResult(null); }}
                className="block w-full mt-1 text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-primary-500 file:text-surface file:text-xs hover:file:bg-primary-600"
                data-testid="documents-file-input"
              />
              <p className="text-xs text-ink-muted mt-1">PDF, DOCX, XLSX, CSV, or image — up to 50 MB.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">Category</label>
              <input aria-label="Category"
                type="text"
                list="documents-category-suggestions"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Financial, HR, Compliance"
                className="block w-full mt-1 px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="documents-category-input"
              />
              <datalist id="documents-category-suggestions">
                {knownCategories.map(c => <option key={c} value={c} />)}
              </datalist>
              <p className="text-xs text-ink-muted mt-1">Must match a category your org has been set up with.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-overline text-ink-muted">Classification</label>
              <select aria-label="Classification"
                value={classification}
                onChange={(e) => setClassification(e.target.value)}
                className="block w-full mt-1 px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
                data-testid="documents-classification-select"
              >
                {CLASSIFICATIONS.map(c => <option key={c} value={c}>{CLASS_LABEL[c]}</option>)}
              </select>
              <p className="text-xs text-ink-muted mt-1">L4 (Restricted) documents become view-only.</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button
              disabled={!file || !category || uploading}
              onClick={upload}
              className="px-4 py-2 bg-primary-500 text-surface rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 motion-organic"
              data-testid="documents-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : <Upload className="w-4 h-4 inline mr-2" />}
              Upload
            </button>
            {uploadResult?.error ? (
              <span className="text-xs text-danger flex items-center gap-1" data-testid="documents-upload-error">
                <AlertTriangle className="w-3.5 h-3.5" /> {uploadResult.error}
              </span>
            ) : uploadResult?.ok ? (
              <span className="text-xs text-ok" data-testid="documents-upload-ok">
                Uploaded · the document now appears in the list below.
              </span>
            ) : null}
          </div>
        </div>

        {/* Document list */}
        <div className="bg-surface border border-surface-border rounded-lg overflow-hidden" data-testid="documents-list">
          <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
            <div className="font-display text-sm flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Documents</div>
            <span className="text-xs text-ink-muted tabular-nums">{docs.length}</span>
          </div>

          {actionError ? (
            <div className="px-4 py-2 bg-danger/5 border-b border-danger/30 text-danger text-xs flex items-center gap-2" data-testid="documents-action-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {actionError}
            </div>
          ) : null}

          {loading ? (
            <div className="p-12 text-center text-ink-muted"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : listError ? (
            <div className="p-8 text-center text-danger text-sm" data-testid="documents-list-error">{listError}</div>
          ) : docs.length === 0 ? (
            <div className="p-12 text-center text-ink-muted text-sm" data-testid="documents-empty">
              <FileText className="w-10 h-10 mx-auto text-ink-subtle mb-3" />
              No documents yet. Upload one above to get started.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-ink-muted uppercase tracking-overline bg-surface-sunken">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Classification</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-right px-4 py-2">Size</th>
                  <th className="text-left px-4 py-2">Uploaded</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  const isL4 = doc.classification === 'L4';
                  const busy = actionBusy === doc.id;
                  return (
                    <tr
                      key={doc.id}
                      className={`border-b border-surface-border last:border-0 ${isL4 ? 'border-l-2 border-l-danger' : ''}`}
                      data-testid={`documents-row-${doc.id}`}
                    >
                      <td className="px-4 py-2 text-ink font-medium" data-testid={`documents-name-${doc.id}`}>
                        {doc.filename || <span className="text-ink-subtle">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-md border ${classBadge(doc.classification)}`} data-testid={`documents-class-${doc.id}`}>
                          {CLASS_LABEL[doc.classification] || doc.classification}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-ink font-medium">{doc.category || '—'}</td>
                      <td className="px-4 py-2 text-xs text-ink-muted">{doc.content_type || '—'}</td>
                      <td className="px-4 py-2 text-right font-data text-ink-muted">{fmtBytes(doc.size_bytes)}</td>
                      <td className="px-4 py-2 text-xs text-ink-muted">{fmtDate(doc.created_at)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {isL4 ? (
                          <button
                            disabled={busy}
                            onClick={() => view(doc)}
                            className="px-3 py-1.5 bg-surface-sunken text-ink-muted rounded-md text-xs hover:bg-warn/10 hover:text-warn disabled:opacity-50 motion-organic inline-flex items-center gap-1.5"
                            title="View inline (download not permitted for Restricted documents)"
                            data-testid={`documents-view-${doc.id}`}
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                            View
                            <Lock className="w-3 h-3 opacity-60" />
                          </button>
                        ) : (
                          <button
                            disabled={busy}
                            onClick={() => download(doc)}
                            className="px-3 py-1.5 bg-surface-sunken text-ink-muted rounded-md text-xs hover:bg-primary-50 hover:text-primary-700 disabled:opacity-50 motion-organic inline-flex items-center gap-1.5"
                            title="Download"
                            data-testid={`documents-download-${doc.id}`}
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            Download
                          </button>
                        )}
                        <button
                          disabled={busy}
                          onClick={() => remove(doc)}
                          className="ml-1.5 px-2.5 py-1.5 bg-surface-sunken text-ink-muted rounded-md text-xs hover:bg-danger/10 hover:text-danger disabled:opacity-50 motion-organic inline-flex items-center gap-1.5"
                          title="Delete"
                          data-testid={`documents-delete-${doc.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-ink-subtle mt-4 flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Access is enforced server-side. Restricted (L4) documents are view-only with no download path.
        </p>
      </div>
    </div>
  );
};

export default Documents;
